import { useEffect, useRef, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle } from "lucide-react";

const DOC_LABEL = { remision: "Remisión", orden: "Orden de trabajo" };

export function CriticalNoticeWatcher() {
  const { user } = useAuth();
  const [notice, setNotice] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const busy = useRef(false);

  const poll = async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const { data } = await api.get("/notices/pending");
      if (Array.isArray(data) && data.length > 0) {
        setNotice((prev) => prev || data[0]);
      }
    } catch (e) {
      /* silencioso */
    } finally {
      busy.current = false;
    }
  };

  useEffect(() => {
    if (!user || user === false) {
      setNotice(null);
      return;
    }
    poll();
    const id = setInterval(poll, 6000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const confirm = async () => {
    if (!notice) return;
    setConfirming(true);
    try {
      await api.post(`/notices/${notice.id}/confirm`);
      setNotice(null);
      setTimeout(poll, 300);
    } catch (e) {
      setNotice(null);
    } finally {
      setConfirming(false);
    }
  };

  if (!notice) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#0B1220]/70 backdrop-blur-sm" data-testid="critical-notice-overlay">
      <div className="w-[92%] max-w-md bg-white border-t-4 border-[#ED1F26] shadow-2xl">
        <div className="flex items-center gap-3 px-6 py-4 bg-[#465B7F]">
          <AlertTriangle className="text-white" size={22} />
          <h2 className="font-display text-white text-lg font-bold">Aviso crítico</h2>
        </div>
        <div className="px-6 py-6 space-y-4">
          <p className="font-mono-data text-sm text-[#16202B]">
            Se ha registrado una <b>{DOC_LABEL[notice.doc_type] || "documento"}</b> que requiere tu atención.
          </p>
          <div className="bg-[#F3F6F9] border border-[#E3E8EE] p-4 space-y-1.5">
            <Row label="Documento" value={`${DOC_LABEL[notice.doc_type] || ""} ${notice.doc_number || ""}`} />
            <Row label="Generado por" value={notice.created_by_name || "—"} />
            <Row label="Fecha" value={fmtDate(notice.created_at)} />
          </div>
          <p className="font-mono-data text-xs text-[#5B6672]">
            Debes confirmar de enterado para continuar usando el sistema.
          </p>
          <button
            onClick={confirm}
            disabled={confirming}
            data-testid="critical-notice-confirm"
            className="w-full bg-[#ED1F26] hover:bg-[#c81820] text-white font-bold font-mono-data text-sm py-3 transition-colors disabled:opacity-60"
          >
            {confirming ? "Confirmando…" : "Confirmar de Enterado"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="overline text-[#5B6672]">{label}</span>
      <span className="font-mono-data text-sm text-[#16202B] text-right">{value}</span>
    </div>
  );
}
